// ============================================================
// DUTY ROTA ENGINE - deterministic generation + verification.
// The AI only translates English into a spec; THIS builds the
// rota, so fairness and exclusions are provable, not hoped for.
//
// spec: {
//   dateFrom, dateTo: 'YYYY-MM-DD'
//   weekdayPool: [names]      // duty rotation Mon-Fri
//   weekendPool: [names]      // duty rotation Sat-Sun
//   exclude: [names]          // hard exclusions (e.g. Captain)
// }
// crew: [{ name, role }]
// absences: [{ name, from, to }]  // ashore, inclusive dates
// ============================================================

function eachDate(from, to) {
  const out = []
  for (let d = new Date(from + 'T12:00:00'); ; d.setDate(d.getDate() + 1)) {
    const iso = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    out.push(iso)
    if (iso === to) break
    if (out.length > 400) break   // guard
  }
  return out
}
const isWeekend = iso => { const g = new Date(iso + 'T12:00:00').getDay(); return g === 0 || g === 6 }

function ashore(name, iso, absences) {
  return absences.some(a => a.name === name && (!a.from || iso >= a.from) && (!a.to || iso <= a.to))
}

export function generateRota(spec, crew, absences = []) {
  const names = new Set(crew.map(c => c.name))
  const clean = pool => (pool || []).filter(n => names.has(n) && !(spec.exclude || []).includes(n))
  const weekdayPool = clean(spec.weekdayPool)
  const weekendPool = clean(spec.weekendPool)
  const pinBy = {}
  ;(spec.pins || []).forEach(pn => { if (pn && pn.date && pn.name) pinBy[pn.date] = pn.name })
  const problems = []
  if (!weekdayPool.length) problems.push('The weekday pool is empty after exclusions.')
  if (!weekendPool.length) problems.push('The weekend pool is empty after exclusions.')
  if (problems.length) return { days: {}, problems, stats: {} }

  const days = {}
  const count = {}                       // total duties per person (fairness)
  const wkndCount = {}                   // weekend duties, balanced separately
  const lastWeekendBy = {}               // Saturday-key of last weekend duty per person
  // A weekend is identified by its Saturday's ISO date. Sunday maps back one day.
  const weekendKey = iso => {
    const d = new Date(iso + 'T12:00:00')
    if (d.getDay() === 0) d.setDate(d.getDate() - 1)
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  }
  const prevWeekendKey = iso => {
    const d = new Date(weekendKey(iso) + 'T12:00:00')
    d.setDate(d.getDate() - 7)
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  }
  const unfilled = []

  for (const iso of eachDate(spec.dateFrom, spec.dateTo)) {
    const wknd = isWeekend(iso)
    if (pinBy[iso]) {
      const pn = pinBy[iso]
      days[iso] = { name: pn, weekend: wknd, pinned: true }
      count[pn] = (count[pn] || 0) + 1
      if (wknd) { lastWeekendBy[pn] = weekendKey(iso); wkndCount[pn] = (wkndCount[pn] || 0) + 1 }
      continue
    }
    const pool = wknd ? weekendPool : weekdayPool
    // candidates: aboard, and for weekends not on duty last weekend
    let cands = pool.filter(n => !ashore(n, iso, absences))
    if (wknd) {
      // Weekend days are assigned individually - Saturday and Sunday go to
      // different people. Nobody takes both days of the same weekend, and
      // nobody takes a weekend day two weekends running.
      const thisWeekend = weekendKey(iso)
      const alreadyThisWeekend = cands.filter(n => lastWeekendBy[n] === thisWeekend)
      let pool2 = cands.filter(n => lastWeekendBy[n] !== thisWeekend)
      if (!pool2.length) pool2 = alreadyThisWeekend      // tiny pool: relax rather than leave the day empty
      const noBackToBack = pool2.filter(n => lastWeekendBy[n] === undefined || lastWeekendBy[n] !== prevWeekendKey(iso))
      cands = noBackToBack.length ? noBackToBack : pool2
    }
    if (!cands.length) { unfilled.push(iso); continue }
    // fairness: fewest duties so far; stable tiebreak by pool order
    cands.sort((a, b) => wknd
      ? ((wkndCount[a] || 0) - (wkndCount[b] || 0) || (count[a] || 0) - (count[b] || 0) || pool.indexOf(a) - pool.indexOf(b))
      : ((count[a] || 0) - (count[b] || 0) || pool.indexOf(a) - pool.indexOf(b)))
    const pick = cands[0]
    days[iso] = { name: pick, weekend: wknd }
    count[pick] = (count[pick] || 0) + 1
    if (wknd) { lastWeekendBy[pick] = weekendKey(iso); wkndCount[pick] = (wkndCount[pick] || 0) + 1 }
  }
  if (unfilled.length) problems.push('No eligible crew for: ' + unfilled.join(', '))
  return { days, problems, stats: count }
}

// Independent verifier - checks the OUTPUT against the spec, not the generator's intentions.
export function verifyRota(rota, spec, crew, absences = []) {
  const errs = []
  const names = new Set(crew.map(c => c.name))
  for (const [iso, d] of Object.entries(rota.days || {})) {
    if (!d || !d.name) continue
    if (d.pinned) continue
    if (!names.has(d.name)) errs.push(iso + ': ' + d.name + ' is not in the crew list')
    if ((spec.exclude || []).includes(d.name)) errs.push(iso + ': ' + d.name + ' was excluded but assigned')
    if (ashore(d.name, iso, absences)) errs.push(iso + ': ' + d.name + ' is ashore that day')
    const pool = isWeekend(iso) ? spec.weekendPool : spec.weekdayPool
    if (pool && pool.length && !pool.includes(d.name)) errs.push(iso + ': ' + d.name + ' is not in the ' + (isWeekend(iso) ? 'weekend' : 'weekday') + ' pool')
  }
  // fairness: within each pool, max-min duty count spread <= 2
  const spread = (poolNames) => {
    const counts = poolNames.map(n => Object.values(rota.days).filter(d => d.name === n).length)
    return counts.length ? Math.max(...counts) - Math.min(...counts) : 0
  }
  const warnings = []
  // A single person carrying the whole rota is legal but worth saying out loud.
  const days = Object.values(rota.days || {}).filter(d => d?.name)
  const people = new Set(days.map(d => d.name))
  if (people.size === 1 && days.length > 7) {
    warnings.push([...people][0] + ' is on duty every single day (' + days.length + '). With this crew there is no one else to share it - consider more crew aboard or fewer duty days.')
  } else if (people.size === 2 && days.length > 20) {
    warnings.push('Only 2 crew are sharing ' + days.length + ' duty days.')
  }
  const wdSpread = spread((spec.weekdayPool || []).filter(n => !(spec.exclude || []).includes(n)))
  if (wdSpread > 2) warnings.push('Duty counts uneven (spread ' + wdSpread + ') - HODs covering weekends naturally take fewer weekdays')
  // no one holds two CONSECUTIVE weekends (Sat+Sun of the same weekend is one duty)
  const satKey = iso => { const d = new Date(iso + 'T12:00:00'); if (d.getDay() === 0) d.setDate(d.getDate() - 1)
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') }
  const byWeekend = {}
  for (const [iso, d] of Object.entries(rota.days || {})) {
    if (!isWeekend(iso) || !d?.name) continue
    const k = satKey(iso)
    byWeekend[k] = byWeekend[k] || new Set(); byWeekend[k].add(d.name)
  }
  const keys = Object.keys(byWeekend).sort()
  const poolSize = (spec.weekendPool || []).filter(x => !(spec.exclude || []).includes(x)).length
  // With split weekends (2 slots per weekend) and a small pool, appearing on
  // consecutive weekends is unavoidable - 3 people cannot cover 8 weekend days
  // any other way. It is worth noting, not a violation.
  for (let i = 1; i < keys.length; i++) {
    const gap = (new Date(keys[i] + 'T12:00:00') - new Date(keys[i-1] + 'T12:00:00')) / 86400000
    if (gap !== 7) continue
    for (const n of byWeekend[keys[i]]) if (byWeekend[keys[i-1]].has(n)) {
      if (poolSize > 4) errs.push(n + ' holds weekend duty two weekends running (' + keys[i-1] + ' and ' + keys[i] + ')')
      else warnings.push(n + ' has weekend duty two weekends running - only ' + poolSize + ' crew in the weekend pool')
    }
  }
  // Saturday and Sunday of the same weekend should be different people
  if (poolSize > 1) {
    const bySat = {}
    for (const [iso, d] of Object.entries(rota.days || {})) {
      if (!isWeekend(iso) || !d?.name) continue
      const k = satKey(iso)
      bySat[k] = bySat[k] || []
      bySat[k].push(d.name)
    }
    for (const [k, names] of Object.entries(bySat)) {
      if (names.length === 2 && names[0] === names[1]) warnings.push(names[0] + ' has both days of the weekend of ' + k)
    }
  }
  return { ok: errs.length === 0, errors: errs, warnings }
}


// ============================================================
// CREW AVAILABILITY - one definition, used by the rota and anything
// else that needs to know who is actually aboard on a given date.
// movements: [{ crew_name, sign_on, sign_off }]
//   sign_on  null => already aboard at the start
//   sign_off null => aboard until further notice
// ============================================================
export function aboardOn(name, iso, movements = []) {
  const mine = movements.filter(m => (m.crew_name || '').trim().toLowerCase() === (name || '').trim().toLowerCase())
  if (!mine.length) return true              // no movement recorded: assume aboard
  // Travel days are not duty days: someone flying out on their sign-off date
  // cannot stand a watch, and someone arriving on their sign-on date is in transit.
  return mine.some(m =>
    (!m.sign_on || iso > m.sign_on) &&
    (!m.sign_off || iso < m.sign_off))
}

// Turn movements into the `absences` shape generateRota expects:
// every day in the range where the person is NOT aboard.
export function absencesFrom(movements, crew, dateFrom, dateTo) {
  const out = []
  for (const c of crew) {
    let start = null
    for (let d = new Date(dateFrom + 'T12:00:00'); d <= new Date(dateTo + 'T12:00:00'); d.setDate(d.getDate() + 1)) {
      const iso = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
      const here = aboardOn(c.name, iso, movements)
      if (!here && !start) start = iso
      if (here && start) { out.push({ name: c.name, from: start, to: prevDay(iso) }); start = null }
    }
    if (start) out.push({ name: c.name, from: start, to: dateTo })
  }
  return out
}
function prevDay(iso) {
  const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() - 1)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
